import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Dimmer, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { SidebarContext } from 'common/contexts/SidebarContext';

// Props with optional `text` argument. Remaining props (notably `data-testid`,
// which AsyncPageContent passes down as `${testId}-loading`) are forwarded to
// the wrapper -- without the spread they were silently dropped.
const LoadingSpinner = ({ text = 'Loading', showBg = false, ...rest }) => {
  // Read sidebar state to align loader to the application pane
  const ctx = useContext(SidebarContext);
  const sidebarIsOpen = ctx?.isOpen ?? true; // default to open when context not provided
  const sidebarWidth = sidebarIsOpen ? 250 : 50;

  const defaultLeft = sidebarIsOpen ? `${sidebarWidth}px` : '0px';
  const defaultWidth = sidebarIsOpen ? `calc(100% - ${sidebarWidth}px)` : '100vw';
  const defaultTop = '0px';
  const defaultHeight = '100vh';

  const [bounds, setBounds] = useState({
    left: defaultLeft,
    width: defaultWidth,
    top: defaultTop,
    height: defaultHeight,
  });

  const updateBounds = useCallback(() => {
    if (typeof document === 'undefined') {
      setBounds({ left: defaultLeft, width: defaultWidth, top: defaultTop, height: defaultHeight });
      return;
    }

    const pageWrapper = document.getElementById('page-content-wrapper');
    if (pageWrapper) {
      const rect = pageWrapper.getBoundingClientRect();
      const left = `${Math.max(0, Math.round(rect.left))}px`;
      const width = `${Math.max(0, Math.round(rect.width))}px`;
      setBounds({ left, width, top: '50px', height: 'calc(100vh - 50px)' });
      return;
    }

    setBounds({ left: defaultLeft, width: defaultWidth, top: defaultTop, height: defaultHeight });
  }, [defaultHeight, defaultLeft, defaultTop, defaultWidth]);

  useEffect(() => {
    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, [updateBounds]);

  return (
    <LoadingWrapper
      $left={bounds.left}
      $width={bounds.width}
      $top={bounds.top}
      $height={bounds.height}
      {...rest}
    >
      <Dimmer active={true} inverted={!showBg}>
        <Loader active inverted={!showBg} size="large">
          {text}
        </Loader>
      </Dimmer>
    </LoadingWrapper>
  );
};

const LoadingWrapper = styled.div`
  position: fixed;
  top: ${props => props.$top || '0px'};
  left: ${props => props.$left || '0px'};
  width: ${props => props.$width || '100vw'};
  height: ${props => props.$height || '100vh'};
  z-index: 9999;
`;

export default LoadingSpinner;
